import React, { useState } from 'react';
import { testPredict } from '../utils/api';

const TestPredictor = () => {
  const [labValues, setLabValues] = useState({ l: '', a: '', b: '' });
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLabValues((prev) => ({ ...prev, [name]: value }));
  };

  const handlePredict = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setPrediction(null);

    try {
      const l = parseFloat(labValues.l);
      const a = parseFloat(labValues.a);
      const b = parseFloat(labValues.b);

      if (isNaN(l) || isNaN(a) || isNaN(b)) {
        throw new Error('유효한 LAB 값을 입력해주세요');
      }

      const result = await testPredict(l, a, b);

      if (result.success) {
        setPrediction(result.data);
      } else {
        throw new Error(result.error || '예측 실패');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 0.8) return 'text-green-600';
    if (prob >= 0.6) return 'text-yellow-600';
    return 'text-orange-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <span className="text-2xl mr-2">🔬</span>
        테스트 예측
      </h3>

      <form onSubmit={handlePredict} className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              L (밝기)
            </label>
            <input
              type="number"
              name="l"
              value={labValues.l}
              onChange={handleInputChange}
              placeholder="0 ~ 100"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              A (빨강-초록)
            </label>
            <input
              type="number"
              name="a"
              value={labValues.a}
              onChange={handleInputChange}
              placeholder="-128 ~ 127"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              B (노랑-파랑)
            </label>
            <input
              type="number"
              name="b"
              value={labValues.b}
              onChange={handleInputChange}
              placeholder="-128 ~ 127"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? '예측 중...' : '예측하기'}
        </button>
      </form>

      {error && (
        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {prediction && (
        <div className="mt-6 space-y-3">
          <h4 className="font-semibold text-gray-800 mb-2">예측 결과:</h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">계절</div>
              <div className="font-semibold text-blue-900 capitalize">
                {prediction.season.prediction}
              </div>
              <div className={`text-sm ${getProbabilityColor(prediction.season.probability)}`}>
                확률: {(prediction.season.probability * 100).toFixed(1)}%
              </div>
            </div>

            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">온도감</div>
              <div className="font-semibold text-orange-900 capitalize">
                {prediction.temperature.prediction}
              </div>
              <div className={`text-sm ${getProbabilityColor(prediction.temperature.probability)}`}>
                확률: {(prediction.temperature.probability * 100).toFixed(1)}%
              </div>
            </div>

            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-xs text-gray-600 mb-1">세부 진단</div>
              <div className="font-semibold text-purple-900 capitalize text-sm">
                {prediction.ai_diagnosis.prediction}
              </div>
              <div className={`text-sm ${getProbabilityColor(prediction.ai_diagnosis.probability)}`}>
                확률: {(prediction.ai_diagnosis.probability * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestPredictor;
